import { Link } from "react-router-dom";
import { ArrowUpRight, UserCheck, RefreshCw, Workflow, Banknote } from "lucide-react";
import { Reveal, MaskLine } from "@/components/Reveal";

const services = [
  {
    n: "01", icon: UserCheck, title: "Permanent Recruitment",
    tag: "Find the right professionals for long-term roles.",
    points: ["Focused candidate sourcing", "Structured screening & evaluation", "Deep role understanding", "Support through offer & closure"],
    cta: "Explore Permanent Recruitment", to: "/services/permanent-recruitment", testid: "services-card-permanent",
  },
  {
    n: "02", icon: RefreshCw, title: "Contract Staffing",
    tag: "Scale your workforce with flexibility.",
    points: ["Skilled contract professionals", "Rapid team scaling", "Workforce flexibility", "Ongoing workforce management"],
    cta: "Explore Contract Staffing", to: "/services/contract-staffing", testid: "services-card-contract",
  },
  {
    n: "03", icon: Workflow, title: "Recruitment Process Outsourcing",
    tag: "Extending your recruitment capabilities with dedicated support.",
    points: ["Dedicated recruitment capacity", "Full or partial lifecycle support", "Screening & shortlisting at scale", "Recruitment tracking & reporting"],
    cta: "Explore RPO", to: "/services/rpo", testid: "services-card-rpo",
  },
  {
    n: "04", icon: Banknote, title: "Payroll Management",
    tag: "Accurate, organized and efficient payroll support.",
    points: ["Salary processing", "Attendance & leave inputs", "Payroll documentation", "Payslip coordination"],
    cta: "Explore Payroll Management", to: "/services/payroll-management", testid: "services-card-payroll",
  },
];

export default function Services() {
  return (
    <>
      <section className="pt-[128px] pb-12 lg:pt-[156px] lg:pb-16" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <MaskLine delay={0.05}>
            <span className="flex items-center gap-4">
              <span className="h-px w-12 bg-brand-red" />
              <span className="font-mono text-[11px] uppercase tracking-[0.38em] text-brand-red font-medium">Services</span>
            </span>
          </MaskLine>
          <h1 className="mt-7 font-display font-extrabold tracking-tight text-slate-900 text-4xl sm:text-6xl lg:text-7xl leading-[1.04] max-w-4xl">
            <MaskLine delay={0.18}>Workforce solutions</MaskLine>
            <MaskLine delay={0.3}>for <span className="text-brand-red">growing teams.</span></MaskLine>
          </h1>
          <MaskLine delay={0.44} className="mt-8 max-w-2xl">
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
              Four connected solutions to build, scale, and run your workforce — every engagement starts the same way: by understanding your business.
            </p>
          </MaskLine>
        </div>
      </section>

      <section className="pb-16 lg:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-6 lg:gap-8">
          {services.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.12}>
              <Link to={s.to} data-testid={s.testid}
                className="group relative block bg-white border border-slate-200 rounded-[1.75rem] p-8 sm:p-12 h-full transition-all duration-500 hover:-translate-y-2 hover:border-brand-red/40 hover:shadow-[0_36px_70px_-30px_rgba(181,51,49,0.3)] overflow-hidden">
                <div className="absolute top-0 left-0 h-1 w-0 bg-brand-red transition-all duration-500 group-hover:w-full" />
                <div className="flex items-start justify-between">
                  <span className="font-display font-extrabold text-5xl text-brand-red/90">{s.n}</span>
                  <span className="p-3.5 rounded-2xl bg-brand-light text-brand-red transition-colors duration-500 group-hover:bg-brand-red group-hover:text-white">
                    <s.icon size={24} />
                  </span>
                </div>
                <h2 className="mt-8 font-display font-bold text-slate-900 text-2xl sm:text-3xl">{s.title}</h2>
                <p className="mt-2 text-sm font-medium text-brand-red">{s.tag}</p>
                <ul className="mt-6 space-y-3">
                  {s.points.map((p) => (
                    <li key={p} className="flex items-center gap-3 text-sm sm:text-base text-slate-600">
                      <span className="h-1.5 w-1.5 rotate-45 bg-brand-red shrink-0" />
                      {p}
                    </li>
                  ))}
                </ul>
                <span className="mt-9 inline-flex items-center gap-2 text-sm font-medium text-slate-900 group-hover:text-brand-red transition-colors">
                  {s.cta}
                  <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </>
  );
}
